import { darken } from 'polished';
import React, { useCallback, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { promptName } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { useResolve } from '../../Container';
import { useEditorSessionObserver } from '../../canvas/EditorSession';
import { ParticipantCounter } from '../../canvas/participants/Counter';
import { FixMenuWrap } from '../../canvas/participants/FixMenuWrap';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { PopperInner, PopperMount } from '../../canvas/ui/PopperElements';
import { InlineWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { Colors, Float, Prompt, remInPx } from './../../../ui';
import { FollowersInfo } from './FollowersInfo';
import { FollowingInfo } from './FollowingInfo';
import { follow } from './PresentCommands';

const XScrollbars = Scrollbars as any;

const Frame = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2.5rem;
    padding: 0 0.75rem;
    margin: 0 0 0.25rem 0.5rem;
    background: ${p => p.color};
    border-radius: 0.25rem;
    color: white;
    font-size: 1.125rem;
    font-weight: 450;
    text-shadow: 0px 0px 1px rgb(0 0 0 / 10%);
    cursor: pointer;
    user-select: none;

    &:hover {
        background: ${p => darken(0.05, p.color)};
    }
    &:active {
        background: ${p => darken(0.10, p.color)};
    }

    svg {
        margin-right: 0.3rem; 
    }
`;

const FollowersItem = styled.div`
    display: flex;
    align-items: center;    
    margin-top: 0.25rem;

    span {
        margin-left: 0.75rem;
    }
`;

const FollowingButton = () => {
    const dispatch = useResolve(CommandDispatcher);
    const ptpts = useResolve(ParticipantManager);
    const { userId } = useEditorSessionObserver(FollowingInfo);

    const onClick = useCallback(() => {
        dispatch(follow, ['']);
    }, []);

    if (!userId) return null;
    const user = ptpts.get(userId);

    return <Frame color={user.color} onClick={onClick}>
        Stop following {user.name}
    </Frame>;
};

const FollowersButton = () => {

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-end',
    });

    const pn = promptName();
    const [open, setOpen] = useState(false);
    const onOpen = useCallback(() => setOpen(!open), [open, setOpen]);
    const onClose = useCallback(() => setOpen(false), [setOpen]);

    const ptpts = useResolve(ParticipantManager);
    const { userIds } = useEditorSessionObserver(FollowersInfo);
    const followers = useMemo(() => ptpts.filter(x => userIds.includes(x.id)), [userIds]);
    if (!followers.length) return null;

    return <>
        <Frame ref={setRefElmt} className={`ignore-${pn}`} color={Colors.primary} onClick={onOpen}>
            <Icon src="deqo://icons/hero/eye" />
            {followers.length} followers
        </Frame>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter onAction={onClose}>
                <PopperInner direction="down">
                    <Flexer padding=".5 0">
                        <Float shadow="lg">
                            <Flexer padding=".75 2.5 .75 .75">
                                <XScrollbars autoHeight autoHeightMax={remInPx(4 * 8)}>
                                    <FixMenuWrap>
                                        <Title>These people are following you:</Title>
                                        {followers.map(x =>
                                            <FollowersItem key={x.id}>
                                                <ParticipantCounter key={x.id} info={x} />
                                                <span>{x.name}</span>
                                            </FollowersItem>
                                        )}
                                    </FixMenuWrap>
                                </XScrollbars>
                            </Flexer>
                        </Float>
                    </Flexer>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </>;
};

export const FollowWidget: LFC<{ widget: InlineWidget }> = ({ widget }) => {
    return <>
        <FollowingButton />
        <FollowersButton />
    </>;
};
